import React, { useContext, useEffect, useState } from 'react';
import { StyledAmenitiesFilterDiv, StyledAmenitiesFilterHeaderContainerDiv } from './index.styles';
import { StyledCheckboxItemDiv, StyledCheckboxItemsContainer } from '../../organisms/searchResults/index.styles';

import { AmenitiesFiltersComponentsProps } from '../../index.types';
import { renderedData } from '../../organisms/searchResults';
import { SearchFilterContext } from '../../index.context';
import { amenityCodeToIconMap, SearchFacetsList, SearchFacetsListCodes } from '../../index.constant';
import { SearchFacetsResponse, SearchFacetsResponseBuckets, SearchStateFilterObj } from '../../../store/store.type';
import { useWindowSize } from '../../../../../../hooks';
import { BREAKPOINT_DESKTOP_1023, BREAKPOINT_TABLET } from '../../../lib/application.constants';
import { formatNumberByLocale } from '../../../../../../../../mi-shop-components/src/utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import clsx from 'clsx';
import { useSessionUpdateContext } from '../../../../../../context/SessionUpdateContext';
import { usePageContext } from '../../../../../../context/PageContext/index';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

export const AmenitiesFilterComponent: React.FC<AmenitiesFiltersComponentsProps> = ({
  onItemSelect,
  allFilterPopUp,
  clearFilterState,
  jsonContents,
  itemList,
}) => {
  const clickTrackingLoc = 'Phoenix Search Results';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { jsonContent }: any = useContext(renderedData);
  const { currentLocale } = usePageContext();
  const jsonContentData = jsonContents ?? jsonContent;
  const { searchQueryState } = useContext(SearchFilterContext);
  const { interactionsSelected, setInteractionsSelected } = useSessionUpdateContext();
  const { width } = useWindowSize();

  const [currCheckedAmenitiesCode, setCurrCheckedAmenitiesCodeObj] = useState<SearchStateFilterObj | undefined>(
    searchQueryState[SearchFacetsList.amenities]
  );

  const getCurrentFilterArray = (): SearchFacetsResponse => {
    // update amenties
    return itemList
      ? itemList
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        searchQueryState?.filter?.filter((key: any) => key.type.code === SearchFacetsListCodes.amenities)[0];
  };

  const [currAvailableAmenities, setCurrAvailableAmenities] = useState<SearchFacetsResponse>(getCurrentFilterArray());

  const DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT = 8;

  // in case available amenties are more than 8, show 8 else show all
  const [hideElementsAfter, setHideElementsAfter] = useState(
    allFilterPopUp && isEmpty(searchQueryState?.AMENITIES ?? {})
      ? DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT
      : currAvailableAmenities?.buckets?.length
  );

  useEffect(() => {
    setCurrAvailableAmenities(getCurrentFilterArray());
  }, [searchQueryState.filter, itemList]);

  useEffect(() => {
    if (clearFilterState === null || clearFilterState === undefined) {
      setCurrCheckedAmenitiesCodeObj(undefined);
    }
  }, [clearFilterState]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelectionChange = (event: any, amenity: SearchFacetsResponseBuckets): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      let obj: SearchStateFilterObj | undefined = { ...currCheckedAmenitiesCode };
      if (obj !== undefined) {
        if (amenity.code in obj) {
          delete obj[amenity.code];
        } else {
          obj[amenity.code] = amenity.code;
        }
      } else {
        obj = {};
        obj[amenity.code] = amenity.code;
      }
      setCurrCheckedAmenitiesCodeObj(obj);
      if (onItemSelect) {
        onItemSelect(SearchFacetsList.amenities, obj);
      }
    }
    setInteractionsSelected({ ...interactionsSelected, amenities: true });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleShowMoreLess = (event: any): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      setHideElementsAfter(
        hideElementsAfter < currAvailableAmenities.buckets?.length
          ? currAvailableAmenities.buckets?.length
          : DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT
      );
    }
  };

  useEffect(() => {
    setCurrCheckedAmenitiesCodeObj(searchQueryState[SearchFacetsList.amenities]);
  }, [searchQueryState?.AMENITIES]);

  return currAvailableAmenities?.type?.count ? (
    <StyledAmenitiesFilterDiv className="t-font-alt-m filter-container">
      {allFilterPopUp ? (
        <fieldset>
          <StyledAmenitiesFilterHeaderContainerDiv className="t-font-alt-m">
            <legend>
              <h4
                className={
                  (width && width > BREAKPOINT_DESKTOP_1023
                    ? `t-subtitle-l`
                    : width && width > BREAKPOINT_TABLET
                    ? `t-subtitle-l`
                    : `t-subtitle-m`) + ' mb-0 filter-heading'
                }
              >
                {jsonContentData?.amenitiesLabel}
              </h4>
            </legend>
            <h6 className="filter-sub-heading">{jsonContentData?.amenitiesSubLabel}</h6>
          </StyledAmenitiesFilterHeaderContainerDiv>
        </fieldset>
      ) : (
        ''
      )}
      <>
        <StyledCheckboxItemsContainer className="checkbox-container">
          {currAvailableAmenities?.buckets
            ?.filter((_amenity: SearchFacetsResponseBuckets, index: number) => index < hideElementsAfter)
            ?.map((amenity: SearchFacetsResponseBuckets, idx: number) => (
              <StyledCheckboxItemDiv className="m-checkbox-selection-item item-container" key={idx}>
                <input
                  onChange={(e): void => onSelectionChange(e, amenity)}
                  onClick={(e): void => onSelectionChange(e, amenity)}
                  className="checkbox-input"
                  type="checkbox"
                  checked={
                    currCheckedAmenitiesCode && amenity.code ? amenity.code in currCheckedAmenitiesCode === true : false
                  }
                  aria-checked={
                    currCheckedAmenitiesCode && amenity.code ? amenity.code in currCheckedAmenitiesCode === true : false
                  }
                  tabIndex={0}
                  onKeyDown={(e): void => onSelectionChange(e, amenity)}
                  id={`${amenity?.code}-checkbox`}
                  aria-hidden
                />
                <label
                  htmlFor={`${amenity?.code}-checkbox`}
                  className="l-margin-top checkbox-label custom_click_track"
                  {...{
                    custom_click_track_value: `${clickTrackingLoc}| Amenity Selection: ${amenity?.label} |internal`,
                  }}
                >
                  <span
                    className={clsx(
                      'icon-s',
                      `icon-${amenityCodeToIconMap[amenity?.code] ?? amenity?.code ?? 'check'}`,
                      'item-icon'
                    )}
                  ></span>
                  <span className="t-font-s item-name">
                    {amenity?.label}{' '}
                    {amenity?.count && (
                      <span className="t-font-s ">({formatNumberByLocale(amenity?.count, currentLocale)})</span>
                    )}
                  </span>
                </label>
              </StyledCheckboxItemDiv>
            ))}
        </StyledCheckboxItemsContainer>
        {allFilterPopUp && currAvailableAmenities.buckets?.length > DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT ? (
          <div>
            <div
              className="t-label-alt-xs show-more-less-option custom_click_track"
              role="button"
              {...{
                custom_click_track_value: `${clickTrackingLoc}| ${
                  hideElementsAfter < currAvailableAmenities.buckets?.length
                    ? jsonContentData.showLessLabel
                    : jsonContentData.showMoreLabel
                } amenities options |internal`,
              }}
              onClick={(e): void => {
                handleShowMoreLess(e);
              }}
              onKeyDown={(e): void => {
                e.stopPropagation();
                handleShowMoreLess(e);
              }}
              tabIndex={0}
              aria-label={
                hideElementsAfter < currAvailableAmenities.buckets?.length
                  ? jsonContentData.showMoreLabel
                  : jsonContentData.showLessLabel
              }
            >
              {hideElementsAfter < currAvailableAmenities.buckets?.length
                ? jsonContentData.showMoreLabel
                : jsonContentData.showLessLabel}
              <div
                className={
                  hideElementsAfter < currAvailableAmenities.buckets?.length ? 'icon-arrow-down' : 'icon-arrow-up'
                }
              ></div>
            </div>
          </div>
        ) : (
          ''
        )}
      </>
      {allFilterPopUp ? <hr aria-hidden className="filter-separator" /> : ''}
    </StyledAmenitiesFilterDiv>
  ) : null;
};
